/**
 * Frontend Logger utility for structured JSON logging
 * Provides production-ready logging interface without console statements
 *
 * NOTE: This file is the logger implementation itself, so console usage
 * is allowed here for development debugging. In production, logs would
 * be sent to a logging service (Sentry, DataDog, etc.)
 */

export interface LogContext {
  [key: string]: unknown;
}

export interface LoggerInterface {
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: Error | unknown, context?: LogContext): void;
  debug(message: string, context?: LogContext): void;
}

class FrontendLogger implements LoggerInterface {
  private formatLog(level: string, message: string, context?: LogContext, error?: Error | unknown) {
    const timestamp = new Date().toISOString();
    const logEntry: Record<string, unknown> = {
      timestamp,
      level: level.toUpperCase(),
      message,
      component: 'frontend',
      ...(context ? { context } : {}),
      ...(error ? { error: this.serializeError(error) } : {}),
    };

    return logEntry;
  }

  private serializeError(error: Error | unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    if (typeof error === 'object' && error !== null) {
      // Plain object (often a LogContext passed as the 2nd arg).
      // Preserve shape instead of coercing to '[object Object]'.
      return { ...(error as Record<string, unknown>) };
    }
    return { message: String(error) };
  }

  private logToStorage(logEntry: Record<string, unknown>): void {
    // In development, log to browser console for debugging
    // In production, this would send to logging service (e.g., Sentry, DataDog)
    if (process.env.NODE_ENV === 'development') {
      const level = logEntry.level as string;
      const message = logEntry.message as string;
      const context = logEntry.context;
      const error = logEntry.error;

      // Use console methods based on log level
      switch (level) {
        case 'ERROR':
          console.error(`[${level}] ${message}`);
          if (error) {
            console.error('Error:', error);
          }
          if (context && Object.keys(context as Record<string, unknown>).length > 0) {
            console.error('Context:', context);
          }
          break;
        case 'WARN':
          console.warn(`[${level}] ${message}`);
          if (context && Object.keys(context as Record<string, unknown>).length > 0) {
            console.warn('Context:', context);
          }
          break;
        case 'DEBUG':
          console.debug(`[${level}] ${message}`);
          if (context && Object.keys(context as Record<string, unknown>).length > 0) {
            console.debug('Context:', context);
          }
          break;
        case 'INFO':
        default:
          console.info(`[${level}] ${message}`);
          if (context && Object.keys(context as Record<string, unknown>).length > 0) {
            console.info('Context:', context);
          }
          break;
      }
    } else {
      // Production: Send to logging service
      // TODO: Integrate with Sentry/DataDog/CloudWatch
      void logEntry;
    }
  }

  info(message: string, context?: LogContext): void {
    this.logToStorage(this.formatLog('info', message, context));
  }

  warn(message: string, context?: LogContext): void {
    this.logToStorage(this.formatLog('warn', message, context));
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    this.logToStorage(this.formatLog('error', message, context, error));
  }

  debug(message: string, context?: LogContext): void {
    this.logToStorage(this.formatLog('debug', message, context));
  }
}

export const logger = new FrontendLogger();
